import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Bot,
  Plus,
  Search,
  Building2,
  Phone,
  Calendar,
  Eye,
  Clock,
  ArrowLeft,
  ArrowRight,
  Check,
  Settings,
  Mic,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  Loader2,
} from 'lucide-react';
import {
  getServiceAgents,
  getServiceAgent,
  createServiceAgent,
  getServiceBusinesses,
  getServicePlans,
  assignSubscriptionToAgent,
} from '../../lib/api';
import { formatDate } from '../../lib/utils';
import {
  formatCount,
  getAgentPlanName,
  getRemainingMessageCount,
  getUsedMessageCount,
} from '../../lib/subscriptionQuota';

const INDUSTRIES = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'construction', label: 'Construction' },
  { value: 'medical', label: 'Medical' },
  { value: 'legal', label: 'Legal' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'retail', label: 'Retail' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'other', label: 'Other' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_BUSINESS_HOURS = {
  monday: { open: '09:00', close: '17:00', enabled: true },
  tuesday: { open: '09:00', close: '17:00', enabled: true },
  wednesday: { open: '09:00', close: '17:00', enabled: true },
  thursday: { open: '09:00', close: '17:00', enabled: true },
  friday: { open: '09:00', close: '17:00', enabled: true },
  saturday: { open: '09:00', close: '13:00', enabled: false },
  sunday: { open: '09:00', close: '13:00', enabled: false },
};

export function ServiceAgentsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadAgents();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      
      const response = await getServiceAgents(params);
      setAgents(response.data.agents);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="service-agents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Voice Agents</h1>
          <p className="text-slate-500">Manage all customer voice agents</p>
        </div>
        <Button onClick={() => navigate('/service/agents/new')} data-testid="service-add-agent-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Agent
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search agents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Agents List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>
          ))}
        </div>
      ) : agents.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Bot className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No agents found</h3>
            <p className="text-slate-500">
              {search ? 'Try adjusting your search' : 'Create your first service-managed agent'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {agents.map((agent) => (
            <Card key={agent.agent_id}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Bot className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-900">{agent.name}</h3>
                        <Badge variant={agent.status === 'active' ? 'default' : 'outline'}>
                          {agent.status}
                        </Badge>
                        {getAgentPlanName(agent) && (
                          <Badge variant="secondary">{getAgentPlanName(agent)}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">Business ID: {agent.business_id}</p>
                      <p className="text-sm text-slate-500">Agent ID: {agent.agent_id}</p>
                      <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                        {agent.customer && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {agent.customer.name}
                          </span>
                        )}
                        {agent.phone_number && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {agent.phone_number}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(agent.created_at)}
                        </span>
                        {getRemainingMessageCount(agent) !== null && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatCount(getRemainingMessageCount(agent))} messages remaining
                          </span>
                        )}
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="text-xs font-medium text-slate-500 mb-2">Subscription</p>
                        {getAgentPlanName(agent) || getRemainingMessageCount(agent) !== null ? (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <p className="text-xs text-slate-500">Plan</p>
                              <p className="text-sm font-medium text-slate-900">
                                {getAgentPlanName(agent) || 'Assigned'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Remaining Messages</p>
                              <p className="text-sm font-medium text-slate-900">
                                {getRemainingMessageCount(agent) !== null
                                  ? formatCount(getRemainingMessageCount(agent))
                                  : '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Used Messages</p>
                              <p className="text-sm font-medium text-slate-900">
                                {getUsedMessageCount(agent) !== null
                                  ? formatCount(getUsedMessageCount(agent))
                                  : '-'}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">No subscription assigned</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/service/agents/${agent.agent_id}/configure`)}
                    >
                      Configure
                    </Button>
                    {agent.customer_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/service/businesses/${agent.customer_id}/twilio?agentId=${encodeURIComponent(agent.agent_id)}`
                          )
                        }
                      >
                        Twilio Config
                      </Button>
                    )}
                    {agent.customer && (
                      <Button variant="outline" size="sm" onClick={() => navigate(`/service/businesses/${agent.customer_id}`)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Business
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => navigate(`/service/agents/${agent.agent_id}`)}>
                      View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function ServiceAgentCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedBusiness = searchParams.get('business');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: preselectedBusiness || '',
    name: '',
    description: '',
    phone_number: '',
    industry: '',
    business_hours: DEFAULT_BUSINESS_HOURS,
    timezone: 'Australia/Sydney',
    additional_details: '',
    greeting_message: '',
    voice_provider: 'elevenlabs',
    voice_id: '',
    system_prompt: '',
    first_message: '',
    plan_id: '',
  });

  useEffect(() => {
    loadBusinesses();
  }, []);

  const loadBusinesses = async () => {
    try {
      const response = await getServiceBusinesses({ limit: 200, skip: 0 });
      setBusinesses(response?.data?.businesses || []);
    } catch (_error) {
      toast.error('Failed to load businesses');
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.customer_id && formData.name && formData.industry;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await createServiceAgent(formData);
      const createdAgentId = response?.data?.agent_id || response?.data?.agent?.agent_id;
      if (!createdAgentId) {
        throw new Error('Agent ID not returned');
      }
      toast.success('Agent created successfully');
      navigate(`/service/agents/${createdAgentId}/configure?fromCreate=1`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, title: 'Basic Info', icon: Bot },
    { num: 2, title: 'Business Hours', icon: Clock },
    { num: 3, title: 'Voice Settings', icon: Mic },
    { num: 4, title: 'Prompts', icon: MessageSquare },
    { num: 5, title: 'Subscription', icon: Settings },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="service-agent-create">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/service/agents')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-jakarta font-bold text-slate-900">Create AI Agent</h1>
        <p className="text-slate-500">Set up a new voice agent for a business</p>
      </div>

      <div className="flex items-center justify-between max-w-3xl">
        {steps.map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-blue-600' : 'text-slate-400'}`}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                  step > s.num ? 'bg-blue-600 border-blue-600 text-white' : step === s.num ? 'border-blue-600 text-blue-600' : 'border-slate-300'
                }`}
              >
                {step > s.num ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className="hidden sm:block text-sm font-medium">{s.title}</span>
            </div>
            {idx < steps.length - 1 && <div className={`w-12 sm:w-20 h-0.5 mx-2 ${step > s.num ? 'bg-blue-600' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>

      <Card className="max-w-3xl">
        <CardContent className="p-6">
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Business *</Label>
                  <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select business" />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses.map((b) => (
                        <SelectItem key={b.user_id || b.business_id} value={b.user_id || b.business_id}>
                          {b.company_name || b.name} ({b.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Agent Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Appointment Booking Agent" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input value={formData.phone_number} onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} placeholder="+61..." />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Industry Type *</Label>
                  <Select value={formData.industry} onValueChange={(value) => setFormData({ ...formData, industry: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>
                          {ind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-4">Business Hours</h2>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select value={formData.timezone} onValueChange={(value) => setFormData({ ...formData, timezone: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Australia/Sydney">Australia/Sydney (AEST)</SelectItem>
                    <SelectItem value="Australia/Melbourne">Australia/Melbourne (AEST)</SelectItem>
                    <SelectItem value="Australia/Brisbane">Australia/Brisbane (AEST)</SelectItem>
                    <SelectItem value="Australia/Perth">Australia/Perth (AWST)</SelectItem>
                    <SelectItem value="Australia/Adelaide">Australia/Adelaide (ACST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                {DAYS.map((day) => (
                  <div key={day} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="w-24">
                      <span className="font-medium capitalize">{day}</span>
                    </div>
                    <Switch
                      checked={formData.business_hours[day]?.enabled}
                      onCheckedChange={(checked) =>
                        setFormData({
                          ...formData,
                          business_hours: { ...formData.business_hours, [day]: { ...formData.business_hours[day], enabled: checked } },
                        })
                      }
                    />
                    {formData.business_hours[day]?.enabled ? (
                      <>
                        <Input
                          type="time"
                          value={formData.business_hours[day]?.open}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              business_hours: { ...formData.business_hours, [day]: { ...formData.business_hours[day], open: e.target.value } },
                            })
                          }
                          className="w-32"
                        />
                        <span>to</span>
                        <Input
                          type="time"
                          value={formData.business_hours[day]?.close}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              business_hours: { ...formData.business_hours, [day]: { ...formData.business_hours[day], close: e.target.value } },
                            })
                          }
                          className="w-32"
                        />
                      </>
                    ) : (
                      <span className="text-slate-500">Closed</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Label>Additional Details for Building Agent</Label>
                <Textarea
                  value={formData.additional_details}
                  onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                  rows={6}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-1">Voice Settings</h2>
              <p className="text-slate-500 text-sm mb-4">Optional: Configure voice and greeting settings</p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Greeting Message</Label>
                  <Textarea value={formData.greeting_message} onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })} rows={3} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Voice Provider</Label>
                    <Select value={formData.voice_provider} onValueChange={(value) => setFormData({ ...formData, voice_provider: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                        <SelectItem value="azure">Azure</SelectItem>
                        <SelectItem value="google">Google</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Voice ID</Label>
                    <Input value={formData.voice_id} onChange={(e) => setFormData({ ...formData, voice_id: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-1">Custom Prompts</h2>
              <p className="text-slate-500 text-sm mb-4">Optional: Define custom prompts for the AI agent</p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>System Prompt</Label>
                  <Textarea value={formData.system_prompt} onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })} rows={6} />
                </div>
                <div className="space-y-2">
                  <Label>First Message</Label>
                  <Textarea value={formData.first_message} onChange={(e) => setFormData({ ...formData, first_message: e.target.value })} rows={3} />
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold mb-1">Subscription Plan</h2>
              <p className="text-slate-500 text-sm mb-4">Create this agent as draft without assigning a plan.</p>
              <div
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  formData.plan_id === '' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setFormData({ ...formData, plan_id: '' })}
              >
                <h3 className="font-semibold text-slate-900">No Plan (Draft)</h3>
                <p className="text-sm text-slate-500">Create agent without a subscription</p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-6 border-t mt-6">
            <Button variant="outline" onClick={() => setStep((s) => s - 1)} disabled={step === 1}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            {step < 5 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canProceed()}>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Agent
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ServiceAgentDetailPage() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  useEffect(() => {
    loadAgent();
  }, [agentId]);

  const loadAgent = async () => {
    try {
      const response = await getServiceAgent(agentId);
      setAgent(response?.data?.agent || response?.data || null);
    } catch (_error) {
      toast.error('Failed to load agent');
      navigate('/service/agents');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid md:grid-cols-2 gap-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (!agent) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="service-agent-detail">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/service/agents')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-jakarta font-bold text-slate-900">{agent.name}</h1>
          <p className="text-slate-500">Agent ID: {agent.agent_id}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate(`/service/agents/${agent.agent_id}/configure`)}>
            <Settings className="w-4 h-4 mr-2" />
            Configure
          </Button>
          {agent.customer_id && (
            <Button
              variant="outline"
              onClick={() => navigate(`/service/businesses/${agent.customer_id}/twilio?agentId=${encodeURIComponent(agent.agent_id)}`)}
            >
              Twilio Config
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Agent Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600">
            <p>Status: <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>{agent.status || 'draft'}</Badge></p>
            <p>Industry: {agent.industry || '-'}</p>
            {agent.phone_number ? <p>Phone: {agent.phone_number}</p> : null}
            {agent.created_at ? <p>Created: {formatDate(agent.created_at)}</p> : null}
            {agent.description ? <p>{agent.description}</p> : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Subscription</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setShowSubscriptionModal(true)}>
              Change Plan
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {getAgentPlanName(agent) || getRemainingMessageCount(agent) !== null ? (
              <>
                <p className="font-medium text-slate-900">{getAgentPlanName(agent) || 'Assigned plan'}</p>
                <p className="text-sm text-slate-500">Remaining messages: {formatCount(getRemainingMessageCount(agent))}</p>
                <p className="text-sm text-slate-500">Used messages: {formatCount(getUsedMessageCount(agent))}</p>
              </>
            ) : (
              <p className="text-sm text-slate-500">No subscription assigned</p>
            )}
          </CardContent>
        </Card>
      </div>

      <ServiceSubscriptionModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        agentId={agentId}
        currentPlanId={agent.subscription?.plan_id}
        onSuccess={() => {
          setShowSubscriptionModal(false);
          loadAgent();
        }}
      />
    </div>
  );
}

function ServiceSubscriptionModal({ open, onClose, agentId, currentPlanId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(currentPlanId || '');

  useEffect(() => {
    if (open) {
      loadPlans();
      setSelectedPlan(currentPlanId || '');
    }
  }, [open, currentPlanId]);

  const loadPlans = async () => {
    try {
      const response = await getServicePlans();
      setPlans(response?.data?.plans || []);
    } catch (_error) {
      toast.error('Failed to load plans');
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    setLoading(true);
    try {
      await assignSubscriptionToAgent(agentId, selectedPlan);
      toast.success('Subscription updated');
      onSuccess();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Change Subscription</DialogTitle>
          <DialogDescription>Select a plan for this agent</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-4 max-h-80 overflow-y-auto">
          {plans.filter((p) => p.is_active).map((plan) => (
            <div
              key={plan.plan_id}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                selectedPlan === plan.plan_id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => setSelectedPlan(plan.plan_id)}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-semibold text-slate-900">{plan.name}</p>
                  <p className="text-sm text-slate-500">{plan.message_count ?? plan.minutes_per_month} messages/month</p>
                </div>
                <p className="text-lg font-bold">${plan.price}</p>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceAgentsPage;
